import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import { formatDate as formatDateLocale } from '../../../utils/dateFormat.js';
import LotDetail, { StatCard } from '../../shared/lots/LotDetail.js';
import DetailWhatsAppTip from '../../shared/DetailWhatsAppTip.js';
import { weightLotTips } from '../../shared/whatsappTips.js';
import LotNavRow from '../../shared/lots/LotNavRow.js';
import ErrorMessage from '../../shared/ErrorMessage.js';
import SuccessMessage from '../../shared/SuccessMessage.js';
import DateInput from '../../shared/inputs/DateInput.js';
import '../../shared/DefaultBadge.css';
import './WeightLots.css';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const s = dateString.length === 10 ? dateString + 'T12:00:00' : dateString;
  const d = new Date(s);
  return isNaN(d) ? dateString : formatDateLocale(d);
};

const formatLotCode = (lotId, prefix = 'WEIGHT') =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '-';

// Mirrors the METHODS list in AddWeightLot.js — kept small and separate
// rather than shared, since only icon+label (not the picker's description)
// is needed here.
const METHOD_BADGES = {
  animal:      { icon: '🐄', labelKey: 'tracking_method_animal' },
  lot_average: { icon: '📋', labelKey: 'tracking_method_lot_average' },
  score:       { icon: '🎯', labelKey: 'tracking_method_score' },
  combination: { icon: '⚙️', labelKey: 'tracking_method_combination' },
};

export const WeightLotDetail = () => {
  const { lotId }  = useParams();
  const { t }      = useTranslation();
  const [lot,               setLot]               = useState(null);
  const [enrollments,       setEnrollments]       = useState([]);
  const [lotWeights,        setLotWeights]        = useState([]);
  const [scores,            setScores]            = useState([]);
  const [sessions,          setSessions]          = useState([]);
  const [slaughter,         setSlaughter]         = useState(null);
  const [showSlaughterForm, setShowSlaughterForm] = useState(false);
  const [slaughterError,    setSlaughterError]    = useState('');
  const [slaughterSuccess,  setSlaughterSuccess]  = useState('');

  useEffect(() => {
    Fetch(`api/data/lots?search_fields=type,lot_id&search_values=weight,${lotId}`, 'get', null,
      (data) => setLot((data || [])[0] || null)
    );
    Fetch(`api/data/lots.animals?search_fields=type,lot_id&search_values=weight,${lotId}`, 'get', null,
      (data) => setEnrollments(data || [])
    );
    Fetch(`api/data/weight.lots.scores?search_fields=lot_id&search_values=${lotId}`, 'get', null,
      (data) => setScores(data || [])
    );
    Fetch(`api/data/weight.lots.weights?search_fields=lot_id&search_values=${lotId}`, 'get', null,
      (data) => setSessions(data || [])
    );
  }, [lotId]);

  useEffect(() => {
    if (!lot) return;
    const code = formatLotCode(lot.lot_id);
    Fetch(`api/data/weight.slaughter?search_fields=lot_code&search_values=${encodeURIComponent(code)}`, 'get', null,
      (data) => setSlaughter((data || [])[0] || null)
    );
  }, [lot]);

  useEffect(() => {
    if (enrollments.length === 0) return;
    Fetch('api/data/weights', 'get', null, (data) => {
      const tags = new Set(enrollments.map(e => e.tag));
      setLotWeights((data || []).filter(w => tags.has(w.tag)));
    });
  }, [enrollments]);

  if (!lot) return <div className="wl-empty-note" style={{ padding: 24 }}>…</div>;

  // Missing/legacy tracking_method decodes as 'combination' — the safe
  // "show everything" fallback that matches this lot's behavior before this
  // field existed, so nothing regresses for lots created before this shipped.
  const method       = lot.weight?.tracking_method || 'combination';
  const showAnimals  = method === 'animal'      || method === 'combination';
  const showSessions = method === 'lot_average' || method === 'combination';
  const showScores   = method === 'score'       || method === 'combination';
  const methodBadge  = METHOD_BADGES[method];

  // Per-tag latest and first weigh-in
  const latestByTag = {};
  const firstByTag  = {};
  lotWeights.forEach(w => {
    if (!w.date) return;
    if (!latestByTag[w.tag] || w.date > latestByTag[w.tag].date) latestByTag[w.tag] = w;
    if (!firstByTag[w.tag]  || w.date < firstByTag[w.tag].date)  firstByTag[w.tag]  = w;
  });

  const latestValues = Object.values(latestByTag).map(w => w.weight);
  const avgCurrentWeight = latestValues.length > 0
    ? (latestValues.reduce((s, v) => s + v, 0) / latestValues.length).toFixed(1)
    : null;

  const adgValues = Object.keys(latestByTag).reduce((acc, tag) => {
    const last  = latestByTag[tag];
    const first = firstByTag[tag];
    if (!first || first.date === last.date) return acc;
    const days = (new Date(last.date) - new Date(first.date)) / 86400000;
    if (days <= 0) return acc;
    acc.push((last.weight - first.weight) / days);
    return acc;
  }, []);
  const avgDailyGain = adgValues.length > 0
    ? (adgValues.reduce((s, v) => s + v, 0) / adgValues.length).toFixed(2)
    : null;

  const latestScore = scores.length > 0
    ? [...scores].sort((a, b) => b.scored_at.localeCompare(a.scored_at))[0]
    : null;

  const animalsSummary = enrollments.length > 0
    ? `${enrollments.length}${avgCurrentWeight ? ` · ${avgCurrentWeight} kg` : ''}`
    : t('no_animals');

  const scoresSummary = latestScore
    ? `${scores.length} · ${t('last_score_short')} ${latestScore.score > 0 ? '+' : ''}${latestScore.score}`
    : t('no_scores');

  const stats = showAnimals && enrollments.length > 0 ? (
    <>
      <StatCard label={t('avg_current_weight')} value={avgCurrentWeight ? `${avgCurrentWeight} kg` : null} />
      <StatCard label={t('adg_column')}          value={avgDailyGain ? `${avgDailyGain} kg/d` : null} />
    </>
  ) : null;

  const handleSlaughterSubmit = (e) => {
    e.preventDefault();
    setSlaughterError('');
    setSlaughterSuccess('');
    const data = new FormData(e.target);
    const body = {
      lot_code:     formatLotCode(lot.lot_id),
      abattoir:     data.get('abattoir') || '',
      yield_pct:    parseFloat(data.get('yield_pct')) || 0,
      animal_count: parseInt(data.get('animal_count'), 10) || 0,
      date:         data.get('date') || '',
    };
    Fetch('api/data/weight.slaughter', 'POST', JSON.stringify(body),
      (saved) => {
        setSlaughter(saved || body);
        setSlaughterSuccess(t('slaughter_record_success'));
        setShowSlaughterForm(false);
      },
      () => setSlaughterError(t('slaughter_record_error'))
    );
  };

  return (
    <>
      <LotDetail
        lot={lot}
        prefix={t('weight_lot_prefix')}
        basePath="/weight/lots"
        onFieldSaved={(field, value) => setLot(prev => ({ ...prev, [field]: value }))}
        stats={stats}
        animalSummary={animalsSummary}
        animalsTo="/weight/animals"
        animalsState={{ lotFilter: lot.lot_id }}
        showAnimals={showAnimals}
        headerBadge={
          <span className="wld-method-badge">
            {methodBadge.icon} {t(methodBadge.labelKey)}
          </span>
        }
        whatsappTip={<DetailWhatsAppTip title={t('lot_tip_title')}
          tips={weightLotTips(formatLotCode(lot.lot_id, t('weight_lot_prefix')), method, t)} />}
      >
        {showSessions && (
          <LotNavRow
            label={t('weightSessions')}
            summary={sessions.length > 0 ? sessions.length : t('no_weigh_ins')}
            to={`/weight/lots/${lotId}/weights`}
          />
        )}
        {showScores && (
          <LotNavRow
            label={t('lot_scores')}
            summary={scoresSummary}
            to={`/weight/lots/${lotId}/scores`}
          />
        )}
      </LotDetail>

      {/* Slaughter — weight-specific, rendered below the shared shell */}
      <div style={{ padding: '0 16px 24px' }}>
        {slaughter ? (
          <div style={{ marginTop: 24 }}>
            <div className="wl-stat-label" style={{ marginBottom: 8, fontWeight: 600, color: '#b8862f' }}>
              {t('slaughter_info')}
            </div>
            <div className="wl-stats-grid">
              <StatCard label={t('date')} value={formatDate(slaughter.date)} />
              {slaughter.abattoir      && <StatCard label={t('slaughter_abattoir')}     value={slaughter.abattoir} />}
              {slaughter.yield_pct > 0 && <StatCard label={t('slaughter_yield_pct')}    value={`${slaughter.yield_pct.toFixed(1)}%`} />}
              {slaughter.animal_count > 0 && <StatCard label={t('slaughter_animal_count')} value={slaughter.animal_count} />}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 24 }}>
            {!showSlaughterForm ? (
              <button className="wld-end-lot-btn" onClick={() => setShowSlaughterForm(true)}>
                {t('end_feed_lot')}
              </button>
            ) : (
              <form className="wld-slaughter-form" onSubmit={handleSlaughterSubmit}>
                <div className="wld-slaughter-title">{t('slaughter_record_title')}</div>
                <input name="abattoir"     type="text"   placeholder={t('slaughter_abattoir')} />
                <input name="yield_pct"    type="number" step="0.1" min="0" max="100"
                       placeholder={t('slaughter_yield_pct')} required />
                <input name="animal_count" type="number" min="0"
                       placeholder={t('slaughter_animal_count')} />
                <DateInput name="date" required />
                <div className="wld-slaughter-actions">
                  <button type="submit">{t('slaughter_record_button')}</button>
                  <button type="button" className="wld-cancel-btn"
                    onClick={() => { setShowSlaughterForm(false); setSlaughterError(''); }}>
                    {t('cancel')}
                  </button>
                </div>
                <ErrorMessage   message={slaughterError}   />
                <SuccessMessage message={slaughterSuccess} />
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default WeightLotDetail;
